import { useState, useEffect } from "react";
import { Dialog, Box } from "@mui/material";
import Select from "react-select";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip, IconButton } from "@mui/material";
import { Boxes, Trash2 } from "lucide-react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { REMOVE_FROM_CART } from "../../../../src/redux/domainAddToCart/domainAddToCart.type";
import axios from "axios";

const CartSubscriptionModal = ({
  subscriptionForm,
  closeSubsciptionModal,
  formErrors,
  formTouched,
  allFieldsValid,
  handleSubscriptionSubmit,
  handleSubscriptionChange,
  showSubscriptionForm,
  loading,
}) => {
  const [subscribableProducts, setSubscribableProducts] = useState([]);
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.domainAddToCart);

  const handleDelete = (id) => {
    dispatch({
      type: REMOVE_FROM_CART,
      payload: id,
    });
  };

  useEffect(() => {
    const fetchSubscribableProducts = async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/subscribable`
      );
      if (response.data) {
        setSubscribableProducts(response.data);
      }
    };
    fetchSubscribableProducts();
  }, []);

  return (
    <Dialog
      open={showSubscriptionForm}
      onClose={closeSubsciptionModal}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: 700, // fixed height
          maxHeight: 700,
        },
      }}
    >
      <div className="flex justify-between h-full bg-blue-50 relative">
        {/* left side */}
        <div className="w-[50%] px-4 py-4 bg-white">
          <Box>
            <h3 className="font-bold text-[18px] text-[#bb1ccc] mb-3">
              Subscription Form
            </h3>

            <form>
              <div className="form-grid">
                <div className="grid grid-cols-1 gap-2 p-2">
                  <div className="mb-3">
                    <label className="text-[14px] font-bold">
                      Target Product Name (Project on Azure/GCP){" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <Select
                      name="productName"
                      value={
                        subscriptionForm.productName
                          ? {
                              value: subscriptionForm.productName,
                              label: subscriptionForm.productName,
                            }
                          : null
                      }
                      onChange={(selectedOption) => {
                        handleSubscriptionChange({
                          target: {
                            name: "productName",
                            value: selectedOption ? selectedOption.value : "",
                            readerGroup: selectedOption?.rg_name,
                            source: selectedOption?.source,
                          },
                        });
                      }}
                      options={
                        subscribableProducts?.map((p) => ({
                          ...p,
                          value: p.product_name,
                          label: `${p.product_name} (${
                            p.source === "TERRAFORM"
                              ? "NOT REGISTERED"
                              : p.source
                          })`,
                          searchText: `${p.product_name} ${
                            p.search_text || ""
                          }`.toLowerCase(),
                        })) || []
                      }
                      placeholder="Search a product"
                      isClearable
                      isSearchable
                      filterOption={(option, inputValue) =>
                        option.data.searchText.includes(
                          inputValue.toLowerCase()
                        )
                      }
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          borderColor: state.isFocused ? "#bb1ccc" : "#e5e7eb",
                          borderRadius: "0.25rem",
                          padding: "2px",
                          boxShadow: state.isFocused
                            ? "0 0 0 1px #bb1ccc"
                            : "none",
                          "&:hover": {
                            borderColor: state.isFocused
                              ? "#bb1ccc"
                              : "#cbd5e1",
                          },
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: "#9ca3af",
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected
                            ? "rgba(0, 0, 160, 0.1)"
                            : state.isFocused
                            ? "rgba(0, 0, 160, 0.05)"
                            : "white",
                          color: state.isSelected ? "#bb1ccc" : "#374151",
                          "&:active": {
                            backgroundColor: "rgba(0, 0, 160, 0.15)",
                          },
                        }),
                      }}
                      noOptionsMessage={() => "No products found"}
                    />
                    {formTouched && formErrors.productName && (
                      <div className="text-red-500 text-xs">
                        {formErrors.productName}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="text-[14px] font-bold">
                      Reader Group (Auto populates){" "}
                      <span className="text-red-500">*</span>
                      <Tooltip
                        arrow
                        placement="right"
                        title={
                          "Reader groups are auto-populated. If you choose to edit them, please ensure the values are correct; otherwise, access will not be granted."
                        }
                        componentsProps={{
                          tooltip: {
                            sx: {
                              fontSize: "0.95rem",
                              lineHeight: 1.5,
                              maxWidth: 320,
                            },
                          },
                        }}
                      >
                        <IconButton size="small">
                          <InfoOutlinedIcon fontSize="medium" />
                        </IconButton>
                      </Tooltip>
                    </label>
                    <input
                      name="readerGroup"
                      value={subscriptionForm.readerGroup}
                      onChange={handleSubscriptionChange}
                      placeholder="Enter Reader Group"
                      className="w-full border rounded p-2 focus:outline focus:outline-2 focus:outline-[#bb1ccc]  h-[42px]"
                    />
                    {subscriptionForm.readerGroup && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {subscriptionForm.readerGroup
                          .split("|")
                          .map((group, index) => (
                            <span
                              key={index}
                              className="bg-[#afaae9] text-white text-xs px-3 py-1 rounded-full"
                            >
                              {group}
                            </span>
                          ))}
                      </div>
                    )}
                    {formTouched && formErrors.readerGroup && (
                      <div className="text-red-500 text-xs">
                        {formErrors.readerGroup}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="text-[14px] font-bold">
                      Usage Duration <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="usageDuration"
                      value={subscriptionForm.usageDuration}
                      onChange={handleSubscriptionChange}
                      className="w-full border rounded p-2 focus:outline focus:outline-2 focus:outline-[#bb1ccc]  h-[42px]"
                    >
                      <option value="">Select Usage Duration</option>
                      <option value="3months">3 Months</option>
                      <option value="6months">6 Months</option>
                      <option value="1year">1 Year</option>
                      <option value="lifetime">Lifetime</option>
                    </select>
                    {formTouched && formErrors.usageDuration && (
                      <div className="text-red-500 text-xs">
                        {formErrors.usageDuration}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="text-[14px] font-bold">
                      Usage Description <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="textarea"
                      name="usageDescription"
                      value={subscriptionForm.usageDescription}
                      onChange={handleSubscriptionChange}
                      placeholder="Describe how you plan to use this data"
                      className="w-full border rounded p-2 focus:outline focus:outline-2 focus:outline-[#bb1ccc]  h-[42px] "
                    />
                    {formTouched && formErrors.usageDescription && (
                      <div className="text-red-500 text-xs">
                        {formErrors.usageDescription}
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="text-[14px] font-bold">
                      Subscribe Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="notificationEmail"
                      value={subscriptionForm.notificationEmail}
                      onChange={handleSubscriptionChange}
                      placeholder="Enter email (@effem.com)"
                      className="w-full border rounded p-2 
             focus:outline focus:outline-2 focus:outline-[#bb1ccc]
             outline-offset-0 h-[42px]"
                    />

                    {formTouched && formErrors.notificationEmail && (
                      <div className="text-red-500 text-xs">
                        {formErrors.notificationEmail}
                      </div>
                    )}
                    {!formTouched &&
                      subscriptionForm.notificationEmail &&
                      formErrors.notificationEmail && (
                        <div className="text-red-500 text-xs mt-1">
                          {formErrors.notificationEmail}
                        </div>
                      )}
                  </div>
                </div>
                <div className="form-consent">
                  <input
                    type="checkbox"
                    name="acceptTerms"
                    checked={subscriptionForm.acceptTerms}
                    onChange={handleSubscriptionChange}
                  />
                  <label>
                    I agree to the data contract terms and will use the data in
                    accordance with the specified quality requirements and usage
                    guidelines.
                  </label>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={closeSubsciptionModal}
                    className="px-4 py-2 bg-gray-200 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubscriptionSubmit}
                    className={`px-4 py-2 rounded ${
                      !subscriptionForm.acceptTerms || !allFieldsValid
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-[#bb1ccc] text-white"
                    } `}
                    disabled={
                      !subscriptionForm.acceptTerms ||
                      !allFieldsValid ||
                      loading
                    }
                  >
                    {loading ? "Submitting.." : "Subcribe"}
                  </button>
                </div>
              </div>
            </form>
          </Box>
        </div>
        {/* right side */}
        <div className="w-[50%] py-4 px-4 flex flex-col h-full mr-2">
          <h3 className="font-bold text-[18px] text-[#bb1ccc] mb-3 shrink-0">
            Selected Assets for subscription(
            {(cartItems && cartItems?.length) || 0})
          </h3>

          <div className="flex flex-col gap-4 overflow-y-auto min-h-0 justify-start">
            {cartItems?.map((item, index) => (
              <div
                key={index}
                className="text-white w-[95%] bg-[#afaae9]  px-2 py-2 rounded-md flex justify-between items-center "
              >
                <div className="flex gap-2 items-center w-[90%] min-w-0 bg-white px-2 py-2 rounded-md">
                  <div className=" bg-[#C6C2F1] px-2 py-2 rounded-md ">
                    <Boxes className="h-5 text-white " />
                  </div>
                  <div className="flex flex-col gap-1 min-w-0">
                    <div className="text-sm text-black break-words">
                      {item?.name || "Asset Name"}
                    </div>

                    <div className="text-sm text-black">
                      Product Name : <strong>{item?.productName}</strong>
                    </div>
                  </div>
                </div>

                <div className="w-[10%] flex justify-end">
                  <Trash2
                    onClick={() => handleDelete(item?.id)}
                    className="h-[40px]  px-1  cursor-pointer  text-red-500"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default CartSubscriptionModal;
